import React, { useCallback, useEffect } from 'react';
import { defineMessages } from 'react-intl.macro';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';
import {
  SvgArrowLeft,
  SvgLock,
  CtaLink,
  DangerAlert,
  Link,
  LINK_SEVERITY,
  SecondaryBtn,
  SIZE,
  SecondaryLink,
  WaitingAlert,
} from '@tmapy/style-guide';
import { ensureArray } from '@tmapy/utils';

import './Detail.sass';

import { setPageTitle } from '../utils/setPageTitle';
import { TypeBadge } from './TypeBadge';
import { actionLoadData, actionSetEndpointQuery } from '../actions';
import { ENDPOINT_DETAIL_DATA, QUERY_STATE } from '../constants';
import { Figure } from './Figure';
import { Tag } from './Tag';
import { DetailPerson } from './DetailPerson';
import { ResourceList } from './ResourceList';
import { Footer } from './Footer';

import { lastCatalogUrlState } from './CatalogList';

const msg = defineMessages({
  backButton: {
    id: 'detail.backButton',
    defaultMessage: 'Zpět na seznam',
  },
  detailSystemTitle: {
    id: 'detail.systemTitle',
    defaultMessage: 'Evidenční systém',
  },
  detailTopicTitle: {
    id: 'detail.topicTitle',
    defaultMessage: 'Téma',
  },
  detailTitle: {
    id: 'detail.type',
    defaultMessage: `{type,select,
            app {Mapové služby}
            data {Distribuce datové sady}
            agenda {Databáze}
            other {}
        }`,
  },
  detailRegionTitle: {
    id: 'detail.regionTitle',
    defaultMessage: 'Územní jednotka',
  },
  detailUpdateTitle: {
    id: 'detail.updateTitle',
    defaultMessage: 'Periodicita aktualizace',
  },
  detailPublicTitle: {
    id: 'detail.publicTitle',
    defaultMessage: 'Zobrazeno veřejnosti',
  },
  detailPublicValueTrue: {
    id: 'detail.publicValueTrue',
    defaultMessage: 'ano',
  },
  detailPublicValueFalse: {
    id: 'detail.publicValueFalse',
    defaultMessage: 'ne',
  },
  detailCuratorTitle: {
    id: 'detail.curatorTitle',
    defaultMessage: 'Kontaktní osoba',
  },
  detailAdministratorTitle: {
    id: 'detail.administratorTitle',
    defaultMessage: 'Správce',
  },
  detailAlertErrorText: {
    id: 'detail.alertErrorText',
    defaultMessage:
      'Nebyl nalezen žádný výsledek. Zkuste, prosím, vyhledávání pomocí klíčových slov.',
  },
  detailWaitingText: {
    id: 'detail.waitingText',
    defaultMessage: 'Načítám data...',
  },
  detailLaunchAppTitle: {
    id: 'detail.launchAppTitle',
    defaultMessage: 'Zobrazit aplikaci',
  },
  detailLaunchAppDisableHelpText: {
    id: 'detail.launchAppDisableHelpText',
    defaultMessage:
      'Nemáte zpřístupněnou aplikaci, pro více informací se obraťte na kontaktní osobu.',
  },
  detailDocLink: {
    id: 'detail.docLink',
    defaultMessage: 'Dokumentace ke stažení',
  },
  detailSystemLink: {
    id: 'detail.systemLink',
    defaultMessage: 'Upravit',
  },
});

export const Detail: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formatMessage = useMessage();
  const uuid = useParams().uuid!;

  const appConfig = useSelector((state) => state.edas.appData.data.config);

  useEffect(() => {
    window.scrollTo(window.scrollX, 0);
    dispatch(actionSetEndpointQuery({ endpoint: ENDPOINT_DETAIL_DATA, query: { uuid } }));
    dispatch(actionLoadData({ endpoint: ENDPOINT_DETAIL_DATA, wipe: true }));
  }, [dispatch, uuid]);

  const detail = useSelector((state) => state.edas.detailData);
  const detailQueryState = detail.queryState;

  const title = `${detail.data.title} – ${appConfig.name} | ${appConfig.city}`;
  useEffect(() => {
    setPageTitle(title);
  }, [title]);

  const handleBackBtnClick = useCallback(
    (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      // Only left mouse button
      if (event.button === 0) {
        navigate('/', { state: lastCatalogUrlState.state });
        event.preventDefault();
      }
    },
    [navigate],
  );

  const topics = ensureArray(detail.data.topics);

  return (
    <>
      <article className='e-u-block sg-a-pt-4 sg-a-pb-4'>
        {detailQueryState === QUERY_STATE.WAITING && (
          <WaitingAlert>
            <FormattedMessage {...msg.detailWaitingText} />
          </WaitingAlert>
        )}

        {!detail.data && detailQueryState === QUERY_STATE.FAIL && (
          <DangerAlert>
            <FormattedMessage {...msg.detailAlertErrorText} />
          </DangerAlert>
        )}

        {detail.data && detailQueryState === QUERY_STATE.SUCCESS && (
          <div className='e-detail'>
            <div className='e-detail--topBar sg-a-d-f sg-a-jc-sb sg-a-ai-b sg-u-hsr-1'>
              <SecondaryBtn
                size={SIZE.SML}
                icon={{ element: <SvgArrowLeft />, position: 'left' }}
                onClick={handleBackBtnClick}
              >
                <FormattedMessage {...msg.backButton} />
              </SecondaryBtn>
            </div>

            <div className='e-detail--col2'>
              <header className='sg-a-d-f sg-a-fw-w sg-a-ai-b sg-a-g-1'>
                <h2 className='sg-u-type-h1 sg-a-mr-1'>{detail.data.title}</h2>
                <div className='sg-a-ml-a'>
                  {detail.data.system_url && (
                    <SecondaryLink href={detail.data.system_url} target='_blank' size={SIZE.SML}>
                      <FormattedMessage {...msg.detailSystemLink} />
                    </SecondaryLink>
                  )}
                  {detail.data.url && (
                    <CtaLink
                      href={detail.data.url}
                      target='_blank'
                      size={SIZE.SML}
                      icon={
                        !detail.data.access ? { element: <SvgLock />, position: 'left' } : undefined
                      }
                      tooltip={
                        !detail.data.access
                          ? formatMessage(msg.detailLaunchAppDisableHelpText)
                          : undefined
                      }
                    >
                      <FormattedMessage {...msg.detailLaunchAppTitle} />
                    </CtaLink>
                  )}
                </div>
              </header>

              <div className='e-detail--imageMaxMed'>
                <Figure
                  src={detail.data.image.url}
                  color={detail.data.image.color}
                  type={detail.data.type}
                />
              </div>

              {detail.data.description && (
                <div
                  className='sg-a-mt-2 e-detail--desc'
                  dangerouslySetInnerHTML={{ __html: detail.data.description }}
                ></div>
              )}

              {detail.data.doc_url && (
                <div className='sg-a-mt-2'>
                  <Link
                    href={detail.data.doc_url}
                    target='_blank'
                    isExternal
                    severity={LINK_SEVERITY.COLORFUL}
                  >
                    {formatMessage(msg.detailDocLink)}
                  </Link>
                </div>
              )}

              <div className='sg-a-mt-2 e-detail--tags'>
                {detail.data.type && (
                  <TypeBadge type={detail.data.type} label={detail.data.type_description} />
                )}
                {detail.data.keywords?.map((item, index) => <Tag key={index} label={item} />)}
              </div>

              {!!detail.data.children?.values.length && (
                <div className='sg-a-mt-4'>
                  <h3 className='sg-a-mb-2 sg-u-type-h2'>{detail.data.children.title}</h3>
                  <ResourceList resources={detail.data.children.values} />
                </div>
              )}
            </div>

            <div className='e-detail--col1'>
              <div className='e-detail--imageMinMed'>
                <Figure
                  src={detail.data.image.url}
                  color={detail.data.image.color}
                  type={detail.data.type}
                />
              </div>

              <div className='e-detail--other'>
                {detail.data.system && (
                  <div>
                    <h3>
                      <FormattedMessage {...msg.detailSystemTitle} />
                    </h3>
                    <p>{detail.data.system}</p>
                  </div>
                )}

                {topics.length > 0 && (
                  <div>
                    <h3>
                      <FormattedMessage {...msg.detailTopicTitle} />
                    </h3>
                    {topics.map((topic, idx) => (
                      <p key={idx}>{topic}</p>
                    ))}
                  </div>
                )}

                {detail.data.region && (
                  <div>
                    <h3>
                      <FormattedMessage {...msg.detailRegionTitle} />
                    </h3>
                    <p>{detail.data.region}</p>
                  </div>
                )}

                {detail.data.update && (
                  <div>
                    <h3>
                      <FormattedMessage {...msg.detailUpdateTitle} />
                    </h3>
                    <p>{detail.data.update}</p>
                  </div>
                )}

                {detail.data.curator?.name && (
                  <div>
                    <h3>
                      <FormattedMessage {...msg.detailCuratorTitle} />
                    </h3>
                    <DetailPerson person={detail.data.curator} />
                  </div>
                )}

                {detail.data.administrator?.name && (
                  <div>
                    <h3>
                      <FormattedMessage {...msg.detailAdministratorTitle} />
                    </h3>
                    <DetailPerson person={detail.data.administrator} />
                  </div>
                )}

                {detail.data.is_public !== undefined && (
                  <div>
                    <h3>
                      <FormattedMessage {...msg.detailPublicTitle} />
                    </h3>
                    <p>
                      <FormattedMessage
                        {...(detail.data.is_public
                          ? msg.detailPublicValueTrue
                          : msg.detailPublicValueFalse)}
                      />
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </article>
      <div className='e-u-block'>
        <Footer />
      </div>
    </>
  );
};
